/**
 * Glossary — what the metrics mean.
 *
 * THE DEFINITIONS ARE NOT IN THIS FILE. They come from /api/glossary, which
 * serves the same GLOSSARY_TERMS list that metric cards and the Jinja
 * templates already read. A TypeScript copy would drift the first time
 * somebody edited one, and two screens quietly disagreeing about what
 * "progressive passes" means is worse than no glossary.
 *
 * Deep links survive: each term keeps its slug as an id, so an existing
 * /glossary#chance-creation link still lands on the right paragraph.
 */
import { useEffect, useMemo, useState } from 'react'

import { ApiError, api } from '../lib/api'
import './glossary.css'

type Term = { slug: string; term: string; definition: string }

export default function Glossary() {
  const [terms, setTerms] = useState<Term[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [query, setQuery] = useState('')

  useEffect(() => {
    api<{ terms: Term[] }>('/api/v1/glossary')
      .then((d) => setTerms(d.terms))
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : String(e)))
  }, [])

  // Scroll to the fragment once the terms are in — the element does not exist
  // at mount, so the browser's own fragment handling has nothing to find.
  useEffect(() => {
    if (!terms || !window.location.hash) return
    document.getElementById(window.location.hash.slice(1))
      ?.scrollIntoView({ block: 'center' })
  }, [terms])

  const shown = useMemo(() => {
    if (!terms) return []
    const sorted = [...terms].sort((a, b) => a.term.localeCompare(b.term))
    const q = query.trim().toLowerCase()
    if (!q) return sorted
    return sorted.filter((t) =>
      t.term.toLowerCase().includes(q) || t.definition.toLowerCase().includes(q))
  }, [terms, query])

  if (error) {
    return (<><h1 className="page-title">Glossary</h1><p className="error">{error}</p></>)
  }
  if (!terms) return <p className="muted">Loading…</p>

  return (
    <>
      <h1 className="page-title">The Priori Glossary</h1>
      <p className="page-sub">
        These are the metrics you’ll commonly see when using Priori.
      </p>

      <input
        className="adm-input gl-search"
        placeholder="Search terms and definitions"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />

      <div className="gl-list">
        {shown.map((t) => (
          <article className="gl-term" id={t.slug} key={t.slug}>
            <h2>{t.term}</h2>
            <p>{t.definition}</p>
          </article>
        ))}
        {shown.length === 0 && (
          <p className="muted">No term matches “{query}”.</p>
        )}
      </div>
    </>
  )
}
